import { ElementRef, Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { VideoDialogComponent } from '../../app/public/video-dialog/video-dialog.component';
import { HelpDialogComponent } from '../../app/public/help-dialog/help-dialog.component';
import { ChickenTestComponent } from '../../app/public/chicken-test/chicken-test.component';
import { DeleteDialogComponent } from '../../app/public/delete-dialog/delete-dialog.component';
import { VesselImageDeleteDialogComponent } from '../../app/public/vessel-image-delete-dialog/vessel-image-delete-dialog.component';
import { TermsOfServiceComponent } from '../../app/public/terms-of-service/terms-of-service.component';
import { CookieStatementComponent } from '../../app/public/cookie-statement/cookie-statement.component';
import { PrivacyPolicyComponent } from '../../app/public/privacy-policy/privacy-policy.component';
import { IndependentContractorAgreementComponent } from '../../app/public/independent-contractor-agreement/independent-contractor-agreement.component';
import { CreateCrewMemberComponent } from '../../app/public/create-crew-member/create-crew-member.component';
import { MarkCharterCompleteComponent } from '../../app/public/mark-charter-complete/mark-charter-complete.component';
import { PublishPaymentsComponent } from '../../app/public/publish-payments/publish-payments.component';
import { PaypalExplanationDialogComponent } from '../../app/public/paypal-explanation/paypal-explanation.component';
import { AddNewCostDialogComponent } from '../../app/portal/charters/manage-charter/add-new-cost/add-new-cost-dialog.component';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { CharterPriceDetailDialogComponent } from '../../app/public/charter-price-detail/charter-price-detail-dialog.component';
import {
  BuyflowConfigLimits,
  BuyflowVesselSortConfig,
} from '../../app/shared/buyflow-sort-filter-control/buyflow-sort-filter-control.component';
import {
  BuyflowSortFilterDialogComponent,
  MobileSortConfig,
} from '../../app/public/buyflow-sort-filter-dialog/buyflow-sort-filter-dialog.component';
import { AppLocation } from '../models/interface/geo';
import { BuyflowMapDialogComponent } from '../../app/public/buyflow-map-dialog/buyflow-map-dialog.component';
import { Moment } from 'moment';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import {
  ChangePayoutPayeeDialogComponent,
  PayoutPayeeSelection,
} from '../../app/public/change-payout-payee-dialog/change-payout-payee-dialog.component';
import { ImageCropperDialogComponent } from '../../app/public/image-cropper-dialog/image-cropper-dialog.component';
import { VesselImage } from 'anchor-shared/lib/model/entity/vesselImage';
import { BucketUploadResponse } from '../pure-functions/upload/image-upload-helper';
import { AddNewTransactionDialogComponent } from '../../app/portal/charters/manage-charter/add-new-transaction-dialog/add-new-transaction-dialog.component';
import { ViewImagesDialogComponent } from '../../app/public/view-images/view-images-dialog.component';
import { CrewMember } from 'anchor-shared/lib/model/entity/crewMember';
import {
  CreatablePayment,
  CreatePayoutFromPaymentDialogComponent,
} from '../../app/portal/charters/manage-charter/create-payout-from-payment-dialog/create-payout-from-payment-dialog.component';
import { Payment } from 'anchor-shared/lib/model/entity/payment';
import { RefundPaymentDialogComponent } from '../../app/portal/charters/manage-charter/refund-payment-dialog/refund-payment-dialog.component';
import { VesselRate } from 'anchor-shared/lib/model/entity/vessel-rate';
import { SubPayout } from 'anchor-shared/lib/model/entity/subPayout';
import { AdminWarningDialogComponent } from '../../app/portal/charters/add/admin-warning/admin-warning-dialog.component';
import {
  BuyflowMapInitialization,
  MapBoundsAndZoom,
} from '../../app/shared/app-buyflow-map/app-buyflow-map.component';
import { CharterConflictRequest } from './dao/vesselAvailabilityPreferencesDAO';
import {
  CaptainSearchComponent,
  CaptainSearchParams,
} from '../../app/portal/profile/captain/charter-captains/captain-search/captain-search.component';
import { VesselTotalPriceDialogComponent } from '../../app/shared/public-components/carousel-vessel-card/vessel-total-price-dialog/vessel-total-price-dialog.component';
import { RequestStripeSignupDialogComponent } from '../../app/shared/dialogs/request-stripe-signup-dialog/request-stripe-signup-dialog.component';
import { StripeInsufficientFundsDialogComponent } from '../../app/shared/dialogs/stripe-insufficient-funds-dialog/stripe-insufficient-funds-dialog.component';
import { PhotoGalleryDialogComponent } from '../../app/shared/dialogs/photo-gallery-dialog/photo-gallery-dialog.component';
import { ProductConflict } from 'anchor-shared/lib/model/interface/productConflict';
import { BuyflowRequestFormFieldDialogComponent } from '../../app/shared/dialogs/buyflow-request-form-field-dialog/buyflow-request-form-field-dialog.component';
import { BuyflowRequestLoginDialogComponent } from '../../app/shared/dialogs/buyflow-request-login-dialog/buyflow-request-login-dialog.component';
import { SecurityHoldPostprocessingDialogComponent } from '../../app/portal/charters/manage-charter/security-hold-postprocessing-dialog/security-hold-postprocessing-dialog.component';
import { ChargeAdditionalCostsDialogComponent } from '../../app/portal/charters/manage-charter/charge-additional-costs-dialog/charge-additional-costs-dialog.component';
import { BookingDescriptionDialogComponent } from '../../app/shared/dialogs/booking-description-dialog/booking-description-dialog.component';
import { CaptainManageDetails } from 'anchor-shared/lib/model/entity/captainManageDetails';
import { HoldProcessingFailureDialogComponent } from '../../app/shared/dialogs/hold-processing-failure-dialog/hold-processing-failure-dialog.component';
import { AmenitiesDialogComponent } from '../../app/shared/dialogs/amenities-dialog/amenities-dialog.component';
import { BoatRulesDialogComponent } from '../../app/shared/dialogs/boat-rules-dialog/boat-rules-dialog.component';
import { BuyflowDepositDialogComponent } from '../../app/shared/dialogs/buyflow-deposit-dialog/buyflow-deposit-dialog.component';
import { BuyflowCancellationDialogComponent } from '../../app/shared/dialogs/buyflow-cancellation-dialog/buyflow-cancellation-dialog.component';
import { BookingDetailsDialogComponent } from '../../app/portal/bookings-manager/booking-details-wrapper/booking-details/booking-details-dialog/booking-details-dialog.component';
import { Discount } from 'anchor-shared/lib/model/entity/discount';
import { CustomerCharterDetailsDialogComponent } from '../../app/shared/dialogs/customer-charter-details-dialog/customer-charter-details-dialog.component';
import { CharterType } from 'anchor-shared/lib/model/enum/charter-type';
import { CustomerAddOnsDialogComponent } from '../../app/shared/dialogs/customer-add-ons-dialog/customer-add-ons-dialog.component';
import { AddOn } from 'anchor-shared/lib/model/entity/addOn';
import { VesselReviewsDialogComponent } from '../../app/public/vessel-reviews/vessel-reviews-dialog/vessel-reviews-dialog.component';
import { CustomerReview } from 'anchor-shared/lib/model/entity/customerReview';
import { AggregateReview } from 'anchor-shared/lib/model/interface/aggregate-review';
import { NavListItem } from '../../app/shared/navigation/navigation.component';
import { ExpandableNavListComponent } from '../../app/shared/navigation/components/mobile-nav-list/expandable-nav-list.component';
import { OptionalAddOnsListDialogComponent } from '../../app/shared/add-ons/optional-add-ons-list-dialog/optional-add-ons-list-dialog.component';
import { GeneralContentDialogComponent } from '../../app/shared/dialogs/general-content-dialog/general-content-dialog.component';

@Injectable()
export class DialogsService {
  constructor(private dialog: MatDialog) {}

  public openGeneralContentDialog(
    content: TemplateRef<any>,
    isMobile: boolean,
    config: {
      width?: string;
      height?: string;
      maxWidth?: string;
      maxHeight?: string;
    } = {},
  ): Observable<any> {
    const dialogRef: MatDialogRef<GeneralContentDialogComponent> =
      this.dialog.open(GeneralContentDialogComponent, {
        data: { content },
        width: config.width || (isMobile ? '100vw' : ''),
        height: config.height || (isMobile ? '100vh' : ''),
        maxWidth: config.maxWidth || (isMobile ? '100vw' : '750px'),
        maxHeight: config.maxHeight || (isMobile ? '100vh' : '80vh'),
        panelClass: 'remove-mat-dialog-padding',
      });

    return dialogRef.afterClosed();
  }

  public VideoDialog(videoId: any): Observable<boolean> {
    let dialogRef: MatDialogRef<VideoDialogComponent>;
    dialogRef = this.dialog.open(VideoDialogComponent, {
      data: { videoId: videoId },
      panelClass: 'transparent',
      autoFocus: false,
    });
    return dialogRef.afterClosed();
  }

  public HelpDialog(
    content: string,
    title?: string,
    subtitle?: string,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<HelpDialogComponent>;
    dialogRef = this.dialog.open(HelpDialogComponent, {
      data: {
        content: content,
        title: title,
        subtitle: subtitle,
      },
    });
    return dialogRef.afterClosed();
  }

  public expandableNavListDialog(navList: NavListItem[]) {
    let dialogRef: MatDialogRef<ExpandableNavListComponent>;
    dialogRef = this.dialog.open(ExpandableNavListComponent, {
      data: {
        navList,
      },
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '100vw',
      height: '100vh',
      panelClass: 'remove-mat-dialog-padding',
    });
    return dialogRef.afterClosed();
  }

  public BookingDescriptionDialog(
    vessel: Vessel,
    isMobile: boolean,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<BookingDescriptionDialogComponent>;
    dialogRef = this.dialog.open(BookingDescriptionDialogComponent, {
      data: {
        vessel: vessel,
      },
      width: isMobile ? '100vw' : '',
      height: isMobile ? '100vh' : '',
      maxWidth: isMobile ? '100vw' : '750px',
      maxHeight: isMobile ? '100vh' : '80vh',
    });
    return dialogRef.afterClosed();
  }

  public AmenitiesDialog(
    vessel: Vessel,
    isMobile: boolean,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<AmenitiesDialogComponent>;
    dialogRef = this.dialog.open(AmenitiesDialogComponent, {
      data: {
        vessel: vessel,
      },
      width: isMobile ? '100vw' : '',
      height: isMobile ? '100vh' : '',
      maxWidth: isMobile ? '100vw' : '750px',
      maxHeight: isMobile ? '100vh' : '80vh',
    });
    return dialogRef.afterClosed();
  }

  public ChickenTest(content: any): Observable<boolean> {
    let dialogRef: MatDialogRef<ChickenTestComponent>;
    dialogRef = this.dialog.open(ChickenTestComponent, {
      data: { content: content },
    });
    return dialogRef.afterClosed();
  }

  public DeleteDialog(filename: string): Observable<boolean> {
    let dialogRef: MatDialogRef<DeleteDialogComponent>;
    dialogRef = this.dialog.open(DeleteDialogComponent, {
      data: { filename: filename },
    });
    return dialogRef.afterClosed();
  }

  public VesselImageDelete(): Observable<boolean> {
    let dialogRef: MatDialogRef<VesselImageDeleteDialogComponent>;
    dialogRef = this.dialog.open(VesselImageDeleteDialogComponent);
    return dialogRef.afterClosed();
  }

  public ImageCropper(
    image: VesselImage,
    vesselId: number,
    oldId: number,
  ): Observable<BucketUploadResponse[]> {
    let dialogRef: MatDialogRef<ImageCropperDialogComponent>;
    dialogRef = this.dialog.open(ImageCropperDialogComponent, {
      data: {
        image: image,
        vesselId: vesselId,
        oldId: oldId,
      },
      maxHeight: '90vh',
    });
    return dialogRef.afterClosed();
  }

  public TermsOfService(): Observable<boolean> {
    let dialogRef: MatDialogRef<TermsOfServiceComponent>;

    dialogRef = this.dialog.open(TermsOfServiceComponent);

    return dialogRef.afterClosed();
  }

  public CookieStatement(): Observable<boolean> {
    let dialogRef: MatDialogRef<CookieStatementComponent>;

    dialogRef = this.dialog.open(CookieStatementComponent);

    return dialogRef.afterClosed();
  }

  public PrivacyPolicy(): Observable<boolean> {
    let dialogRef: MatDialogRef<PrivacyPolicyComponent>;

    dialogRef = this.dialog.open(PrivacyPolicyComponent);

    return dialogRef.afterClosed();
  }

  public ICA(): Observable<boolean> {
    let dialogRef: MatDialogRef<IndependentContractorAgreementComponent>;

    dialogRef = this.dialog.open(IndependentContractorAgreementComponent);

    return dialogRef.afterClosed();
  }

  public CreateCrewMember(): Observable<any> {
    let dialogRef: MatDialogRef<CreateCrewMemberComponent>;

    dialogRef = this.dialog.open(CreateCrewMemberComponent);

    return dialogRef.afterClosed();
  }

  public MarkCharterComplete(
    captainManageDetails: CaptainManageDetails,
    depositAmount: string,
    depositPayment: Payment,
  ): Observable<any> {
    let dialogRef: MatDialogRef<MarkCharterCompleteComponent>;

    dialogRef = this.dialog.open(MarkCharterCompleteComponent, {
      data: {
        captainManageDetails: captainManageDetails,
        depositAmount: depositAmount,
        depositPayment,
      },
    });

    return dialogRef.afterClosed();
  }

  public HoldProcessingFailure(
    failureReason: string,
    userId: number,
    failedAmount: string,
    paymentIntentId: string,
  ): Observable<any> {
    let dialogRef: MatDialogRef<HoldProcessingFailureDialogComponent>;

    dialogRef = this.dialog.open(HoldProcessingFailureDialogComponent, {
      data: {
        failureReason: failureReason,
        userId: userId,
        failedAmount: failedAmount,
        paymentIntentId: paymentIntentId,
      },
    });

    return dialogRef.afterClosed();
  }

  public PublishPayments(): Observable<PublishPaymentsComponent> {
    let dialogRef: MatDialogRef<PublishPaymentsComponent>;

    dialogRef = this.dialog.open(PublishPaymentsComponent);

    return dialogRef.afterClosed();
  }

  public PaypalExplanation(): Observable<PaypalExplanationDialogComponent> {
    let dialogRef: MatDialogRef<PaypalExplanationDialogComponent>;

    dialogRef = this.dialog.open(PaypalExplanationDialogComponent);

    return dialogRef.afterClosed();
  }

  public SelectPayoutPayeeDialog(
    payout: Partial<Payout> | SubPayout,
    crew: CrewMember[],
    charter?: Partial<Charter>,
  ): Observable<PayoutPayeeSelection> {
    let dialogRef: MatDialogRef<ChangePayoutPayeeDialogComponent>;

    dialogRef = this.dialog.open(ChangePayoutPayeeDialogComponent, {
      data: { payout: payout, crew: crew, charter: charter },
    });

    return dialogRef.afterClosed();
  }

  public AddNewCostDialog(
    charter: Charter,
    costToEdit?: any,
    costPaid: boolean = false,
  ): Observable<any> {
    let dialogRef: MatDialogRef<AddNewCostDialogComponent>;

    dialogRef = this.dialog.open(AddNewCostDialogComponent, {
      data: {
        charter,
        costToEdit,
        costPaid,
      },
    });

    return dialogRef.afterClosed();
  }

  public CharterPriceDetailDialog(
    type: string = 'all',
    payouts: Payout[],
    durationType: 0 | 4 | 8,
    customNumberOfHours: number,
    vessel: Vessel,
    discounts: any[],
    services: any[],
    isMobile: boolean,
    rates?: VesselRate[],
    charter?: Charter,
    showDuration?: boolean,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<CharterPriceDetailDialogComponent>;

    dialogRef = this.dialog.open(CharterPriceDetailDialogComponent, {
      data: {
        payouts,
        durationType,
        customNumberOfHours,
        vessel,
        discounts,
        services,
        rates,
        charter,
        type,
        showDuration,
      },
      width: isMobile ? '100vw' : '400px',
      height: isMobile ? '100vh' : '',
      maxWidth: isMobile ? '100vw' : '750px',
      maxHeight: isMobile ? '100vh' : '80vh',
      panelClass: 'remove-mat-dialog-padding',
    });

    return dialogRef.afterClosed();
  }

  public BookingsDetailsDialog(
    charter: Charter,
    payoutBreakdowns: any[],
    discounts: Discount[],
    type,
    isMobile: boolean,
    target: ElementRef,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<BookingDetailsDialogComponent>;
    dialogRef = this.dialog.open(BookingDetailsDialogComponent, {
      data: {
        charter,
        payoutBreakdowns,
        discounts,
        type,
        target,
      },
      width: isMobile ? '100vw' : '',
      height: isMobile ? '100vh' : '',
      maxWidth: isMobile ? '100vw' : '750px',
      maxHeight: isMobile ? '100vh' : '80vh',
      panelClass: 'remove-mat-dialog-padding',
    });
    return dialogRef.afterClosed();
  }

  public CustomerCharterDetailsDialog(
    charter: Charter,
    isMobile: boolean,
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(CustomerCharterDetailsDialogComponent, {
      data: {
        charter,
      },
      width: isMobile ? '100vw' : '400px',
      height: isMobile ? '100vh' : '',
      maxWidth: isMobile ? '100vw' : '750px',
      maxHeight: isMobile ? '100vh' : '80vh',
      panelClass: 'remove-mat-dialog-padding',
    });
    return dialogRef.afterClosed();
  }

  public VesselReviewsDialog(
    vesselReviews: CustomerReview[],
    aggregateReviews: AggregateReview[],
    isMobile: boolean,
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(VesselReviewsDialogComponent, {
      data: {
        vesselReviews,
        aggregateReviews,
        isMobile,
      },
      width: isMobile ? '100vw' : '60vw',
      height: isMobile ? '100vh' : '90vh',
      maxWidth: isMobile ? '100vw' : '1000px',
      maxHeight: isMobile ? '100vh' : '90vh',
      panelClass: 'no-padding-or-overflow',
    });
    return dialogRef.afterClosed();
  }

  public CustomerAddOnsDialog(
    addOns: AddOn[],
    charterBasePrice: number,
    requiredAddOnsTotal: number,
    availableFrom: Moment,
    availableTo: Moment,
    duration: number,
    charter: Charter,
    createAdjustment,
    isMobile,
    loading: boolean,
  ): MatDialogRef<CustomerAddOnsDialogComponent> {
    let dialogRef: MatDialogRef<CustomerAddOnsDialogComponent>;

    dialogRef = this.dialog.open(CustomerAddOnsDialogComponent, {
      data: {
        addOns,
        charterBasePrice,
        requiredAddOnsTotal,
        availableFrom,
        availableTo,
        duration,
        charter,
        createAdjustment,
        isMobile,
        loading,
      },
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: !isMobile && 'no-padding-or-overflow',
    });

    return dialogRef;
  }

  public OptionalAddOnsListDialog(
    addOns: AddOn[],
    isMobile: boolean,
  ): MatDialogRef<OptionalAddOnsListDialogComponent> {
    let dialogRef: MatDialogRef<OptionalAddOnsListDialogComponent>;

    dialogRef = this.dialog.open(OptionalAddOnsListDialogComponent, {
      data: {
        addOns,
      },
      width: isMobile ? '100vw' : '400px',
      height: isMobile ? '100vh' : '400px',
      maxWidth: isMobile ? '100vw' : '700px',
      maxHeight: isMobile ? '100vh' : '60vh',
    });

    return dialogRef;
  }

  public BuyflowSortFilterDialog(
    limits: BuyflowConfigLimits,
    previousConfigValues: BuyflowVesselSortConfig,
    previousSortValue: string[],
    availableOn: Moment,
  ): Observable<MobileSortConfig> {
    let dialogRef: MatDialogRef<BuyflowSortFilterDialogComponent>;

    dialogRef = this.dialog.open(BuyflowSortFilterDialogComponent, {
      width: '100vh',
      height: '100vh',
      maxWidth: '100vh',
      maxHeight: '100vh',
      data: {
        limits: limits,
        previousConfigValues: previousConfigValues,
        previousSortValue: previousSortValue,
        availableOn: availableOn,
      },
    });

    return dialogRef.afterClosed();
  }

  public BuyflowMapDialog(
    vessels: Vessel[],
    location: AppLocation,
    updateMapBounds: BehaviorSubject<MapBoundsAndZoom>,
    boundedAndFiltered: BehaviorSubject<{ vessels: Vessel[] }>,
    buyflowMapInitialization: BuyflowMapInitialization,
  ) {
    let dialogRef = this.dialog.open(BuyflowMapDialogComponent, {
      panelClass: 'custom-map-container',
      width: '100vh',
      height: '95vh',
      maxWidth: '100vh',
      maxHeight: '100vh',
      data: {
        vessels: vessels,
        location: location,
        updateMapBounds: updateMapBounds,
        boundedAndFilteredResult: boundedAndFiltered,
        buyflowMapInitialization: buyflowMapInitialization,
      },
    });

    return dialogRef.afterClosed();
  }

  public CreatePayoutFromPayment(payment: CreatablePayment, charter: Charter) {
    let dialogRef: MatDialogRef<CreatePayoutFromPaymentDialogComponent>;

    dialogRef = this.dialog.open(CreatePayoutFromPaymentDialogComponent, {
      data: { payment: payment, charter: charter },
      width: '65vw',
      maxWidth: '700px',
      minWidth: '700px',
      maxHeight: '80vh',
    });

    return dialogRef.afterClosed();
  }

  public ViewImagesDialog(imageUrlToOpen: string) {
    let dialogRef = this.dialog.open(ViewImagesDialogComponent, {
      // width: '75vw',
      // height: '75vh',
      // maxWidth: '75vw',
      // maxHeight: '75vh',
      data: {
        imageUrlToOpen: imageUrlToOpen,
      },
    });

    return dialogRef.afterClosed();
  }

  public AddNewTransactionDialog(
    charter: Charter,
    userId: number,
    remainderStatus: string,
    depositPayment: Payment,
    fullPayment: Payment,
    isMobile?: boolean,
    readOnlyTransactionData?,
    transactionType?: string,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<AddNewTransactionDialogComponent>;

    dialogRef = this.dialog.open(AddNewTransactionDialogComponent, {
      data: {
        charter,
        userId,
        remainderStatus,
        depositPayment,
        fullPayment,
        isMobile,
        readOnlyTransactionData,
        transactionType,
      },
      width: isMobile ? '100vw' : '500px',
      height: isMobile ? '100vh' : '1000px',
      maxWidth: isMobile ? '100vw' : '700px',
      maxHeight: isMobile ? '100vh' : '80vh',
    });
    return dialogRef.afterClosed();
  }

  public refundTransactionDialog(
    payment: Payment,
    refundAmount: number,
    validator,
    onSubmit,
    options: {},
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<RefundPaymentDialogComponent>;

    dialogRef = this.dialog.open(RefundPaymentDialogComponent, {
      data: { payment, onSubmit, validator, options, refundAmount },
      maxWidth: '500px',
      maxHeight: '80vh',
    });
    return dialogRef.afterClosed();
  }

  public boatRulesDialog(
    instructionsAndRules: string,
    isMobile: boolean,
    charterType: CharterType = CharterType.DayCharter,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<BoatRulesDialogComponent>;

    dialogRef = this.dialog.open(BoatRulesDialogComponent, {
      data: { instructionsAndRules: instructionsAndRules, charterType },
      width: isMobile ? '100vw' : '',
      height: isMobile ? '100vh' : '',
      maxWidth: isMobile ? '100vw' : '750px',
      maxHeight: isMobile ? '100vh' : '80vh',
    });
    return dialogRef.afterClosed();
  }

  public buyflowDepositDialog(
    depositAmount: number,
    isMobile: boolean,
    charterType: CharterType = CharterType.DayCharter,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<BuyflowDepositDialogComponent>;

    dialogRef = this.dialog.open(BuyflowDepositDialogComponent, {
      data: { depositAmount: depositAmount, charterType },
      width: isMobile ? '100vw' : '',
      height: isMobile ? '100vh' : '',
      maxWidth: isMobile ? '100vw' : '750px',
      maxHeight: isMobile ? '100vh' : '80vh',
    });
    return dialogRef.afterClosed();
  }

  public buyflowCancellationPolicyDialog(
    isMobile: boolean,
    charterType: CharterType = CharterType.DayCharter,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<BuyflowCancellationDialogComponent>;

    dialogRef = this.dialog.open(BuyflowCancellationDialogComponent, {
      data: { charterType },
      width: isMobile ? '100vw' : '',
      height: isMobile ? '100vh' : '',
      maxWidth: isMobile ? '100vw' : '750px',
      maxHeight: isMobile ? '100vh' : '80vh',
    });
    return dialogRef.afterClosed();
  }

  public vesselListPriceDialog(
    halfDayPrice: number,
    halfDayDiscount: boolean,
    halfDayOriginalTotalPrice: number,
    fullDayPrice: number,
    fullDayDiscount: boolean,
    fullDayOriginalTotalPrice: number,
    halfDayLineItems: VesselRate[],
    fullDayLineItems: VesselRate[],
    vesselName: string,
    vesselId: number,
    isDateSelected: boolean,
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<VesselTotalPriceDialogComponent>;

    dialogRef = this.dialog.open(VesselTotalPriceDialogComponent, {
      data: {
        halfDayPrice: halfDayPrice,
        halfDayDiscount: halfDayDiscount,
        halfDayOriginalTotalPrice: halfDayOriginalTotalPrice,
        fullDayPrice: fullDayPrice,
        fullDayDiscount: fullDayDiscount,
        fullDayOriginalTotalPrice: fullDayOriginalTotalPrice,
        halfDayLineItems: halfDayLineItems,
        fullDayLineItems: fullDayLineItems,
        vesselName: vesselName,
        vesselId: vesselId,
        isDateSelected: isDateSelected,
      },
      width: '65vw',
      maxWidth: '370px',
      minWidth: '370px',
      maxHeight: '75vh',
    });
    return dialogRef.afterClosed();
  }

  public adminWarningDialog(
    conflictRequest: CharterConflictRequest,
    productConflict: ProductConflict[],
  ): Observable<boolean> {
    let dialogRef: MatDialogRef<AdminWarningDialogComponent>;

    dialogRef = this.dialog.open(AdminWarningDialogComponent, {
      data: {
        conflictRequest: conflictRequest,
        productConflict: productConflict,
      },
      maxWidth: '550px',
      maxHeight: '80vh',
    });
    return dialogRef.afterClosed();
  }

  public headCaptainSerachDialog(
    stateWaterBodyMappings: any[],
    searchParams: CaptainSearchParams,
  ) {
    let dialogRef: MatDialogRef<CaptainSearchComponent>;
    dialogRef = this.dialog.open(CaptainSearchComponent, {
      data: {
        stateWaterBodyMappings: stateWaterBodyMappings,
        searchParams: searchParams,
      },
      maxWidth: '700px',
      maxHeight: '80vh',
    });
    return dialogRef.afterClosed();
  }

  public requestStripeSignUpDialog() {
    let dialogRef: MatDialogRef<RequestStripeSignupDialogComponent>;
    dialogRef = this.dialog.open(RequestStripeSignupDialogComponent, {
      maxWidth: '350px',
      maxHeight: '80vh',
    });
    return dialogRef.afterClosed();
  }

  public stripeInsufficientFundsDialog(isProd: boolean) {
    let dialogRef: MatDialogRef<StripeInsufficientFundsDialogComponent>;
    dialogRef = this.dialog.open(StripeInsufficientFundsDialogComponent, {
      data: {
        isProd: isProd,
      },
      maxWidth: '350px',
      maxHeight: '80vh',
    });
    return dialogRef.afterClosed();
  }

  public PhotoGalleryDialog(vesselImages: VesselImage[], vesselVideo: string) {
    let dialogRef: MatDialogRef<PhotoGalleryDialogComponent>;
    dialogRef = this.dialog.open(PhotoGalleryDialogComponent, {
      data: {
        vesselImages: vesselImages,
        vesselVideo: vesselVideo,
      },
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '100vw',
      height: '100vh',
    });
    return dialogRef.afterClosed();
  }

  public BuyflowRequestFormFieldDialog(requestData: any) {
    let dialogRef: MatDialogRef<BuyflowRequestFormFieldDialogComponent>;
    dialogRef = this.dialog.open(BuyflowRequestFormFieldDialogComponent, {
      data: {
        requestData: requestData,
      },
    });
    return dialogRef.afterClosed();
  }

  public BuyflowRequestLoginDialog(email: string, isMobile: boolean) {
    let dialogRef: MatDialogRef<BuyflowRequestLoginDialogComponent>;
    dialogRef = this.dialog.open(BuyflowRequestLoginDialogComponent, {
      data: {
        userEmail: email,
      },
      width: isMobile ? '100vw' : '',
      height: isMobile ? '100vh' : '',
      maxWidth: isMobile ? '100vw' : '75vw',
      maxHeight: isMobile ? '100vh' : '75vh',
    });
    return dialogRef.afterClosed();
  }

  public OauthResetDialog(oAuthProvider: string, isMobile: boolean) {
    let dialogRef: MatDialogRef<BuyflowRequestLoginDialogComponent>;
    dialogRef = this.dialog.open(BuyflowRequestLoginDialogComponent, {
      data: {
        oAuthProvider: oAuthProvider,
        oAuthMode: 'true',
      },
      maxWidth: isMobile ? '100vw' : '60vw',
      maxHeight: isMobile ? '100vh' : '60vh',
    });
    return dialogRef.afterClosed();
  }

  public SecurityHoldPostprocessingDialog(
    depositPayment: Payment,
    isRefund: boolean,
    charter?: Charter,
    refundAmount?: number,
    fullPayment?: Payment,
  ) {
    let dialogRef: MatDialogRef<SecurityHoldPostprocessingDialogComponent>;
    dialogRef = this.dialog.open(SecurityHoldPostprocessingDialogComponent, {
      data: {
        depositPayment: depositPayment,
        isRefund: isRefund,
        charter: charter,
        refundAmount: refundAmount,
        fullPayment: fullPayment,
      },
      maxWidth: '60vw',
      width: '40vw',
      maxHeight: '60vh',
      height: '40vh',
    });
    return dialogRef.afterClosed();
  }

  public ChargeAdditionalCostsDialog(
    charter: Charter,
    hoursTotal: number,
    fuelTotal: number,
    costsTotal: number,
    depositTotal: number,
  ) {
    let dialogRef: MatDialogRef<ChargeAdditionalCostsDialogComponent>;
    dialogRef = this.dialog.open(ChargeAdditionalCostsDialogComponent, {
      data: {
        charter,
        hoursTotal,
        fuelTotal,
        costsTotal,
        depositTotal,
      },
      maxWidth: '60vw',
      width: '30vw',
      maxHeight: '60vh',
      height: '30vh',
    });
    return dialogRef.afterClosed();
  }

  public SecurityHoldDialog(depositAmount: number) {
    this.HelpDialog(
      `A hold (not a charge) will be placed on your credit card 48 hours before your booking starts to cover any incidental damage that may occur during your rental. This hold is released 48 hours after the booking is complete,
      if no claims are made.Deposit amountThis owner requires a $${depositAmount} security deposit. A hold (not a charge) for $${depositAmount} will be placed on your credit card 48 hours before your departure date`,
      'Security Deposit',
      depositAmount.toString(),
    );
  }
}
